import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Image,
  Badge,
  Modal,
  AspectRatio,
  Group,
  Stack,
  ActionIcon,
  Overlay,
  Center,
  Button,
  Box,
  ThemeIcon,
} from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import {
  IconX,
  IconBrandYoutube,
  IconPhoto,
  IconArticle,
  IconChevronRight,
  IconPlayerPlay,
  IconSpeakerphone,
} from "@tabler/icons-react";
import { PerformRequest } from "../../hooks/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import Footer from "../../components/Footer";
import "./styles.scss";
import "@mantine/carousel/styles.css";

type AnnouncementType = "youtube" | "image" | "carousel" | "text";

type Announcement = {
  _id: string;
  title: string;
  description?: string;
  type: AnnouncementType;
  mediaUrls: string[];
  thumbnail: string;
  isActive: boolean;
  createdAt: string;
};

const Announcements = () => {
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<Announcement | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchAnnouncements = useCallback(async (pageNum: number) => {
    setLoading(true);

    const limit = 9;
    try {
      // Use PerformRequest directly
      const { data, status } = await PerformRequest<{ data: Announcement[] }>({
        method: Endpoints.GetAnnouncements({
          activeOnly: true,
          page: pageNum,
          limit,
        }).method,
        route: Endpoints.GetAnnouncements({
          activeOnly: true,
          page: pageNum,
          limit,
        }).route,
      });

      if (status === 200 && data?.data) {
        const newData = data.data;
        if (newData.length < limit) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }

        setAnnouncements((prev) =>
          pageNum === 1 ? newData : [...prev, ...newData],
        );
      }
    } catch (error) {
      console.error("Failed to fetch announcements", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements(1);
  }, [fetchAnnouncements]);

  const onLoadMore = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchAnnouncements(nextPage);
  }, [page, fetchAnnouncements]);

  useEffect(() => {
    if (!hasMore || loading) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;

      // Trigger when user scrolls within 200px of bottom
      if (scrollTop + windowHeight >= docHeight - 200) {
        onLoadMore();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loading, page, onLoadMore]);

  const getThumbnail = (announcement: Announcement) => {
    // Priority 1: If it's YouTube, try to generate high-quality thumb from URL
    if (announcement.type === "youtube" && announcement.mediaUrls[0]) {
      const regExp =
        /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = announcement.mediaUrls[0].match(regExp);
      const videoId = match && match[2].length === 11 ? match[2] : null;
      if (videoId)
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    // Priority 2: Use explicitly saved thumbnail
    if (announcement.thumbnail) return announcement.thumbnail;
    // Priority 3: Fallback to first media URL
    return announcement.mediaUrls[0];
  };

  const getTypeIcon = (type: AnnouncementType) => {
    switch (type) {
      case "youtube":
        return <IconBrandYoutube size={16} />;
      case "image":
        return <IconPhoto size={16} />;
      case "carousel":
        return <IconPhoto size={16} />;
      case "text":
        return <IconArticle size={16} />;
      default:
        return <IconSpeakerphone size={16} />;
    }
  };

  const getTypeColor = (type: AnnouncementType) => {
    switch (type) {
      case "youtube":
        return "red";
      case "image":
        return "green";
      case "carousel":
        return "blue";
      case "text":
        return "violet";
      default:
        return "gray";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="announcements-page">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-bg">
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
        </div>
        <Container
          size="xl"
          py={80}
          style={{ position: "relative", zIndex: 2 }}
        >
          <Stack align="center" gap="md">
            <Badge
              variant="gradient"
              gradient={{ from: "green", to: "lime" }}
              size="lg"
              radius="md"
              className="hero-badge"
            >
              News & Updates
            </Badge>
            <Title
              order={1}
              size={60}
              fw={900}
              ta="center"
              style={{ lineHeight: 1.1 }}
            >
              Latest from <span className="gradient-text">Virel</span>
            </Title>
            <Text size="xl" c="dimmed" ta="center" maw={600} mx="auto">
              Stay up to date with the latest features, community highlights,
              and platform updates.
            </Text>
          </Stack>
        </Container>
      </div>
      {/* Content Grid */}
      <Container size="xl" py={60}>
        {loading && page === 1 ? (
          <Text ta="center" size="lg" c="dimmed">
            Loading updates...
          </Text>
        ) : announcements.length === 0 ? (
          <Stack align="center" py={100}>
            <ThemeIcon size={80} radius="xl" variant="light" color="gray">
              <IconSpeakerphone size={40} />
            </ThemeIcon>
            <Title order={3}>No announcements yet</Title>
            <Text c="dimmed">Check back later for updates!</Text>
          </Stack>
        ) : (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing={30}>
              {announcements.map((item) => {
                const thumbnail = getThumbnail(item);
                return (
                  <Card
                    key={item._id}
                    padding="none"
                    radius="lg"
                    className="announcement-card"
                    onClick={() => setSelectedAnnouncement(item)}
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.05)",
                      backdropFilter: "blur(10px)",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      className="card-image-wrapper"
                      style={{
                        height: 220,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <Image
                        src={thumbnail}
                        h={220}
                        w="100%"
                        fit="cover"
                        className="card-image"
                        fallbackSrc="https://placehold.co/600x400?text=No+Thumbnail"
                        style={{ transition: "transform 0.5s ease" }}
                      />
                      <Overlay
                        gradient="linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 100%)"
                        opacity={0.9}
                        zIndex={1}
                      />

                      <Badge
                        className="card-type-badge"
                        color={getTypeColor(item.type)}
                        variant="filled"
                        leftSection={getTypeIcon(item.type)}
                        style={{
                          position: "absolute",
                          top: 12,
                          left: 12,
                          zIndex: 2,
                        }}
                      >
                        {item.type.toUpperCase()}
                      </Badge>

                      {item.type === "youtube" && (
                        <Center
                          className="card-play-overlay"
                          style={{ position: "absolute", inset: 0, zIndex: 2 }}
                        >
                          <div
                            className="play-circle"
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              backgroundColor: "rgba(255,255,255,0.2)",
                              backdropFilter: "blur(4px)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconPlayerPlay size={24} fill="white" stroke={0} />
                          </div>
                        </Center>
                      )}
                    </div>

                    <Box p="lg" style={{ position: "relative", zIndex: 2 }}>
                      <Text size="xs" c="dimmed" fw={500} mb={6}>
                        {formatDate(item.createdAt)}
                      </Text>
                      <Title
                        order={4}
                        c="white"
                        fw={700}
                        lh={1.3}
                        mb={8}
                        lineClamp={2}
                      >
                        {item.title}
                      </Title>
                      <Text size="sm" c="dimmed" lineClamp={2} lh={1.5} mb="lg">
                        {item.description || "Tap to view details"}
                      </Text>

                      <Group justify="space-between" align="center">
                        <Text
                          size="sm"
                          fw={600}
                          c="green.4"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          Read More <IconChevronRight size={14} />
                        </Text>
                      </Group>
                    </Box>
                  </Card>
                );
              })}
            </SimpleGrid>

            {hasMore && (
              <Center mt={50}>
                <Button
                  onClick={onLoadMore}
                  loading={loading}
                  variant="light"
                  color="green"
                  size="md"
                  radius="xl"
                >
                  Load More
                </Button>
              </Center>
            )}
          </>
        )}
      </Container>
      <Footer />
      {/* Detail Modal */}
      <Modal
        opened={!!selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
        size="lg"
        centered
        padding={0}
        radius="lg"
        withCloseButton={false}
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      >
        {selectedAnnouncement && (
          <Box className="modal-content">
            <ActionIcon
              className="modal-close-btn"
              variant="white"
              onClick={() => setSelectedAnnouncement(null)}
              size="lg"
              radius="xl"
              style={{ position: "absolute", right: 10, top: 10, zIndex: 10 }}
            >
              <IconX size={20} />
            </ActionIcon>

            <Box className="modal-media-section">
              {selectedAnnouncement.type === "youtube" ? (
                <AspectRatio ratio={16 / 9}>
                  <iframe
                    src={`https://www.youtube.com/embed/${
                      selectedAnnouncement.mediaUrls[0]
                        ?.split("v=")[1]
                        ?.split("&")[0] ||
                      selectedAnnouncement.mediaUrls[0]?.split("youtu.be/")[1]
                    }?autoplay=1`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </AspectRatio>
              ) : selectedAnnouncement.type === "carousel" ? (
                <Carousel withIndicators height={320}>
                  {selectedAnnouncement.mediaUrls.map((url, i) => (
                    <Carousel.Slide key={i}>
                      <Image src={url} h={320} fit="cover" />
                    </Carousel.Slide>
                  ))}
                </Carousel>
              ) : (
                <Image
                  src={getThumbnail(selectedAnnouncement)}
                  h={320}
                  fit="cover"
                />
              )}
            </Box>

            <Box p="xl">
              <Group justify="space-between" mb="xs">
                <Badge
                  color={getTypeColor(selectedAnnouncement.type)}
                  variant="light"
                  size="lg"
                >
                  {selectedAnnouncement.type.toUpperCase()}
                </Badge>
                <Text size="sm" c="dimmed">
                  {formatDate(selectedAnnouncement.createdAt)}
                </Text>
              </Group>

              <Title order={2} mb="md">
                {selectedAnnouncement.title}
              </Title>

              {selectedAnnouncement.description && (
                <Text size="md" style={{ whiteSpace: "pre-line" }} lh={1.6}>
                  {selectedAnnouncement.description}
                </Text>
              )}
            </Box>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default Announcements;
